import { MouseEventHandler, useRef, useState } from 'react';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Placement as BootstrapOverlayPlacement } from 'react-bootstrap/Overlay';

import useCopyToClipboard from 'hooks/useCopyToClipboard';
import IcomoonIcon from 'components/common/Icon/IcomoonIcon';
import { ICON_COLORS } from 'styles/stylesProvider/themes';

import useTextOverflow from './useTextOverflow';

import './TruncateWithTooltip.scss';

export enum TooltipPlacement {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum EllipsisPlacement {
  BEGINNING,
  END,
}

type TruncateWithTooltipProps = {
  id: string;
  text: string;
  placement?: TooltipPlacement;
  ellipsisPlacement?: EllipsisPlacement;
  classNames?: string;
};

export const TruncateWithTooltip = ({
  id,
  text,
  placement = TooltipPlacement.TOP,
  ellipsisPlacement = EllipsisPlacement.END,
  classNames,
}: TruncateWithTooltipProps): JSX.Element => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useTextOverflow({ containerRef, text });
  const copy = useCopyToClipboard();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const copyToClipboard: MouseEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    copy(text);
  };

  return (
    <>
      {isOverflowing ? (
        <div className="TruncateWithTooltipContainer" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <OverlayTrigger
            key={text}
            placement={placement as BootstrapOverlayPlacement}
            overlay={<Tooltip id={id}>{text}</Tooltip>}
          >
            <div
              className={classnames(
                'TruncateWithTooltip',
                {
                  'TruncateWithTooltip--RTL': ellipsisPlacement === EllipsisPlacement.BEGINNING,
                },
                classNames,
              )}
              ref={containerRef}
            >
              {text}
            </div>
          </OverlayTrigger>
          {isHovered && (
            <OverlayTrigger
              key={`${text}-copyToClipboard`}
              placement={placement as BootstrapOverlayPlacement}
              overlay={<Tooltip id={`${id}-copyToClipboard`}>Copy to clipboard</Tooltip>}
            >
              <span className="TruncateWithTooltip__CopyToClipboard" onClick={copyToClipboard}>
                <IcomoonIcon name="Copy-icon" size={16} color={ICON_COLORS.LINK} />
              </span>
            </OverlayTrigger>
          )}
        </div>
      ) : (
        <div className={classnames('TruncateWithTooltip', classNames)} ref={containerRef}>
          {text}
        </div>
      )}
    </>
  );
};
